 "use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useOpenWithIntegrationCheck } from "../users/useOpenWithIntegrationCheck";
import {
  CalendarSource,
  ICalendar,
  ICalendarAuth,
  Organization,
  OrganizationSettings,
  ResponseWithStatus
} from "@gettactic/api";
import { getMSTwoWayUrl } from "@gettactic/helpers";
import { MS_1_MINUTE } from "../../defines";
export const useRoomReservation = ({
  isOpen,
  isRoomReservation: mayNeedDelegatedAccess,
  calendar,
  organizationSettings,
  slug,
  integrationsUrl,
  apiClient
}: {
  isOpen: boolean;
  isRoomReservation: boolean;
  calendar: ICalendar | null;
  organizationSettings: OrganizationSettings | null;
  slug: Organization["slug"];
  integrationsUrl: string;
  apiClient: {
    getMicrosoftPermissions: () => Promise<ResponseWithStatus<ICalendarAuth>>;
  };
}) => {
  const { hasMSCalendar, hasGoogleCalendar } = useMemo(() => {
    return {
      hasMSCalendar: calendar?.source === CalendarSource.Microsoft,
      hasGoogleCalendar: calendar?.source === CalendarSource.Google
    };
  }, [calendar?.source]);

  const [requireDelegatedPermission, setRequireDelegatedPermission] =
    useState(false);
  const hasCheckedPermissions = useRef(false);

  const msAuthUrl = getMSTwoWayUrl(integrationsUrl || "", slug || "");

  const shouldCheckForDelegatedAccess = useMemo(() => {
    return organizationSettings?.calendar_settings
      ?.microsoft_enable_delegated_user_access;
  }, [organizationSettings]);

  const { getShouldRequestDelegatedAccess } = useOpenWithIntegrationCheck({
    enabled: mayNeedDelegatedAccess && shouldCheckForDelegatedAccess,
    apiClient,
    refetchInterval: MS_1_MINUTE
  });

  // Check permissions when modal opens for room reservations in creation mode
  useEffect(() => {
    if (!isOpen) {
      // Reset when modal is closed
      setRequireDelegatedPermission(false);
      hasCheckedPermissions.current = false;
      return;
    }

    // If the room does not have a Microsoft calendar, no permission needed
    if (!hasMSCalendar) {
      setRequireDelegatedPermission(false);
      return;
    }

    // For other than room creation reservations, no permission needed
    if (!mayNeedDelegatedAccess) {
      setRequireDelegatedPermission(false);
      return;
    }

    // Skip if already checked permissions in this session
    if (hasCheckedPermissions.current) {
      return;
    }

    const checkPermissions = async () => {
      if (shouldCheckForDelegatedAccess) {
        const shouldRequestDelegatedAccess =
          await getShouldRequestDelegatedAccess();

        // Simply set the flag based on the check result
        setRequireDelegatedPermission(shouldRequestDelegatedAccess);
        hasCheckedPermissions.current = true;
      } else {
        setRequireDelegatedPermission(false);
      }
    };

    checkPermissions();
  }, [
    mayNeedDelegatedAccess,
    shouldCheckForDelegatedAccess,
    getShouldRequestDelegatedAccess,
    isOpen,
    hasMSCalendar
  ]);

  return {
    requireDelegatedPermission,
    msAuthUrl,
    hasMSCalendar,
    hasGoogleCalendar
  };
};
